import { useEffect, useState } from "react";
import { http, formatErr } from "../../lib/api";
import { fileToDataUrl, stripDataUrl } from "../../lib/files";
import { usePolling } from "../../lib/usePolling";
import { useOpenParam } from "../../lib/useOpenParam";
import { formatDateIT } from "../../lib/labels";
import { toast } from "sonner";
import { Plus, FileUp, CheckCircle2, Clock } from "lucide-react";

const EMPTY = { title: "", category: "", description: "", stage: "attivo", audience: "all", auto_assign_new: true, link: "", filename: "", data_b64: "", content_type: "", recipient_ids: [] };

export default function CrmMateriali() {
  const [list, setList] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [acks, setAcks] = useState(null);
  const [agents, setAgents] = useState([]);
  const [nv, setNv] = useState(null);

  const load = async () => { const { data } = await http.get("/crm/materials"); setList(data); };
  const openId = useOpenParam(list);
  useEffect(() => {
    load().catch(() => {});
    http.get("/crm/agents").then((r) => setAgents(r.data || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  usePolling(load, { interval: 10000, enabled: true });

  const on = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const pickFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error("File troppo grande (max 5MB).");
    const d = await fileToDataUrl(file);
    setForm((s) => ({ ...s, filename: file.name, data_b64: stripDataUrl(d), content_type: file.type || "application/pdf" }));
  };

  const create = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await http.post("/crm/materials", form);
      toast.success(`Materiale pubblicato (assegnato a ${data.assigned_now} agenti).`);
      setForm(EMPTY); setShowForm(false); await load();
    } catch (err) { toast.error(formatErr(err)); }
    setBusy(false);
  };

  const openAcks = async (m) => { const { data } = await http.get(`/crm/materials/${m.id}/acknowledgements`); setAcks({ title: m.title, ...data }); };
  const pickNvFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error("File troppo grande (max 5MB).");
    const d = await fileToDataUrl(file);
    setNv((s) => ({ ...s, filename: file.name, data_b64: stripDataUrl(d), content_type: file.type || "application/pdf" }));
  };
  const newVersion = async (e) => {
    e.preventDefault();
    if (!nv.data_b64 && !nv.link) return toast.error("Carica il nuovo file o indica un link.");
    setBusy(true);
    try {
      const { data } = await http.post(`/crm/materials/${nv.id}/new-version`, { title: nv.title, description: nv.description, link: nv.link || null, filename: nv.filename, data_b64: nv.data_b64, content_type: nv.content_type });
      setNv(null); await load(); toast.success(`Versione ${data.version} pubblicata: nuova presa visione richiesta a ${data.assigned_now} agenti.`);
    } catch (err) { toast.error(formatErr(err)); }
    setBusy(false);
  };

  return (
    <div data-testid="crm-materiali-page" className="max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-5">
        <h1 className="font-serif text-3xl">Materiali di vendita</h1>
        <button onClick={() => setShowForm((v) => !v)} data-testid="crm-material-new" className="px-4 py-2 rounded-md text-white text-sm flex items-center gap-2" style={{ background: "var(--brand-dark)" }}><Plus className="w-4 h-4" /> Nuovo materiale</button>
      </div>

      {showForm && (
        <form onSubmit={create} className="card-clean p-5 mb-5 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm" data-testid="crm-material-form">
          <F label="Titolo *" value={form.title} onChange={on("title")} testid="cm-title" required />
          <F label="Categoria" value={form.category} onChange={on("category")} testid="cm-category" />
          <label className="block md:col-span-2"><span className="text-[10px] uppercase tracking-widest text-neutral-500 block mb-1">Descrizione</span>
            <textarea value={form.description} onChange={on("description")} data-testid="cm-desc" className="w-full px-3 py-2 rounded-md border" style={{ borderColor: "var(--border-silver)" }} /></label>
          <label className="block"><span className="text-[10px] uppercase tracking-widest text-neutral-500 block mb-1">Disponibilità</span>
            <select value={form.stage} onChange={on("stage")} data-testid="cm-stage" className="w-full px-3 py-2 rounded-md border" style={{ borderColor: "var(--border-silver)" }}><option value="attivo">Dopo attivazione</option><option value="candidatura">Durante candidatura</option></select></label>
          <label className="block"><span className="text-[10px] uppercase tracking-widest text-neutral-500 block mb-1">Destinatari</span>
            <select value={form.audience} onChange={on("audience")} data-testid="cm-audience" className="w-full px-3 py-2 rounded-md border" style={{ borderColor: "var(--border-silver)" }}><option value="all">Tutti gli agenti idonei (in base allo stato)</option><option value="selected">Solo agenti selezionati</option></select>
            <span className="text-[10px] text-neutral-400 mt-1 block">"Idonei" = agenti ammessi secondo lo stato scelto (durante candidatura o dopo attivazione).</span>
          </label>
          {form.audience === "selected" && (
            <div className="md:col-span-2 border rounded-md p-3" style={{ borderColor: "var(--border-silver)" }} data-testid="cm-recipients">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">Seleziona gli agenti destinatari</div>
              {agents.length === 0 ? <div className="text-xs text-neutral-400" data-testid="cm-no-agents">Nessun agente disponibile da selezionare.</div> : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
                  {agents.map((a) => (
                    <label key={a.id} className="flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={form.recipient_ids.includes(a.id)} data-testid={`cm-recipient-${a.id}`}
                        onChange={(e) => setForm((st) => ({ ...st, recipient_ids: e.target.checked ? [...st.recipient_ids, a.id] : st.recipient_ids.filter((x) => x !== a.id) }))} />
                      {a.profile?.first_name ? `${a.profile.first_name} ${a.profile.last_name || ""}` : a.email}
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}
          <F label="Link (opzionale)" value={form.link} onChange={on("link")} testid="cm-link" />
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.auto_assign_new} onChange={(e) => setForm({ ...form, auto_assign_new: e.target.checked })} data-testid="cm-autoassign" /> Assegna automaticamente ai nuovi agenti</label>
          <label className="px-3 py-2 rounded-md border text-xs flex items-center gap-2 cursor-pointer w-fit" style={{ borderColor: "var(--border-silver)" }} data-testid="cm-file-label"><FileUp className="w-3 h-3" /> {form.filename || "Carica file"}<input type="file" className="hidden" onChange={pickFile} data-testid="cm-file" /></label>
          <div className="md:col-span-2"><p className="text-[11px] text-neutral-400 mb-2">La pubblicazione richiede agli agenti destinatari la <strong>presa visione obbligatoria</strong> del materiale.</p><button type="submit" disabled={busy} data-testid="crm-material-save" className="px-4 py-2 rounded-md text-white text-sm" style={{ background: "var(--brand-primary)" }}>Pubblica</button></div>
        </form>
      )}

      <div className="card-clean overflow-hidden" data-testid="crm-materials-list">
        {list.length === 0 ? <div className="p-6 text-sm text-neutral-500">Nessun materiale.</div> : (
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[10px] uppercase tracking-widest text-neutral-500 border-b" style={{ borderColor: "var(--border-silver)" }}>
              <th className="px-4 py-3">Titolo</th><th className="px-4 py-3">Disp.</th><th className="px-4 py-3">v</th><th className="px-4 py-3">Prese visione</th><th className="px-4 py-3">Azioni</th>
            </tr></thead>
            <tbody>
              {list.map((m) => (
                <tr key={m.id} id={`open-${m.id}`} className={`border-b last:border-0 ${openId === m.id ? "bg-neutral-50" : ""}`} style={{ borderColor: "var(--border-silver)" }} data-testid={`crm-material-row-${m.id}`}>
                  <td className="px-4 py-3 font-medium">{m.title}<div className="text-[11px] text-neutral-400">{m.category}</div></td>
                  <td className="px-4 py-3 text-neutral-500">{m.stage === "candidatura" ? "Durante candidatura" : "Dopo attivazione"}</td>
                  <td className="px-4 py-3">{m.version}</td>
                  <td className="px-4 py-3">{m.acknowledged}/{m.assignments}</td>
                  <td className="px-4 py-3 flex gap-2">
                    <button onClick={() => openAcks(m)} data-testid={`crm-material-acks-${m.id}`} className="px-2 py-1 rounded border text-xs" style={{ borderColor: "var(--border-silver)" }}>Dettaglio</button>
                    <button onClick={() => setNv({ id: m.id, title: m.title, description: m.description || "", link: "", filename: "", data_b64: "", content_type: "", version: m.version })} data-testid={`crm-material-newver-${m.id}`} className="px-2 py-1 rounded border text-xs" style={{ borderColor: "var(--border-silver)" }}>Nuova versione</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {nv && (
        <form onSubmit={newVersion} className="card-clean p-5 mt-5 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm" data-testid="crm-material-newver-form">
          <h2 className="font-serif text-lg md:col-span-2">Nuova versione (v{nv.version + 1}) — {nv.title}</h2>
          <label className="block md:col-span-2"><span className="text-[10px] uppercase tracking-widest text-neutral-500 block mb-1">Descrizione</span>
            <textarea value={nv.description} onChange={(e) => setNv({ ...nv, description: e.target.value })} data-testid="cmv-desc" className="w-full px-3 py-2 rounded-md border" style={{ borderColor: "var(--border-silver)" }} /></label>
          <F label="Link (opzionale)" value={nv.link} onChange={(e) => setNv({ ...nv, link: e.target.value })} testid="cmv-link" />
          <label className="px-3 py-2 rounded-md border text-xs flex items-center gap-2 cursor-pointer w-fit h-fit self-end" style={{ borderColor: "var(--border-silver)" }} data-testid="cmv-file-label"><FileUp className="w-3 h-3" /> {nv.filename || "Carica nuovo file"}<input type="file" className="hidden" onChange={pickNvFile} data-testid="cmv-file" /></label>
          <div className="md:col-span-2 flex gap-2"><button type="submit" disabled={busy} data-testid="cmv-save" className="px-4 py-2 rounded-md text-white text-sm" style={{ background: "var(--brand-primary)" }}>Pubblica nuova versione</button><button type="button" onClick={() => setNv(null)} data-testid="cmv-cancel" className="px-4 py-2 rounded-md border text-sm" style={{ borderColor: "var(--border-silver)" }}>Annulla</button></div>
          <p className="text-[11px] text-neutral-400 md:col-span-2">La nuova versione richiede una nuova presa visione obbligatoria; le conferme precedenti restano nello storico.</p>
        </form>
      )}

      {acks && (
        <div className="card-clean p-5 mt-5" data-testid="crm-material-acks-panel">
          <div className="flex items-center justify-between mb-3"><h2 className="font-serif text-lg">Prese visione — {acks.title} (v{acks.version})</h2><button onClick={() => setAcks(null)} data-testid="crm-material-acks-close" className="text-xs underline">chiudi</button></div>
          {acks.rows.length === 0 ? <p className="text-sm text-neutral-500">Nessuna assegnazione.</p> : acks.rows.map((r) => (
            <div key={r.id} className="flex items-center gap-2 text-sm py-1" data-testid={`crm-material-ack-row-${r.id}`}>
              {r.acknowledged ? <CheckCircle2 className="w-4 h-4 text-green-600" /> : <Clock className="w-4 h-4 text-amber-500" />}
              {r.agent_name || r.agent_email} — v{r.version} {r.acknowledged ? `(confermato ${formatDateIT(r.acknowledged_at)})` : "(in attesa)"}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function F({ label, value, onChange, testid, required }) {
  return (
    <label className="block"><span className="text-[10px] uppercase tracking-widest text-neutral-500 block mb-1">{label}</span>
      <input value={value} onChange={onChange} required={required} data-testid={testid} className="w-full px-3 py-2 rounded-md border" style={{ borderColor: "var(--border-silver)" }} /></label>
  );
}
